import { NativeStackScreenProps } from "@react-navigation/native-stack";
import {
  Card,
  FlatActionList,
  FlatActionListItem,
  Page,
  Stack,
  View,
} from "@towersystems/roam-common-native";
import { SettingsRootParamList } from "../../../../types";
import {
  useActiveLocation,
  useActiveTerminal,
} from "../../../../utilities/app-state/hooks";
import { PaymentMethodSettingListCard } from "./components/PaymentMethodSettingListCard";
import { formatDateToCalendar } from "@towersystems/roam-common/lib/shared-utils";
import { useFocusEffect } from "@react-navigation/native";
import React from "react";
import { useTranslation } from "../../../../utilities/i18n";
import packageInfo from "../../../../../package.json";
import {
  Text,
  TextButton,
} from "@towersystems/roam-common-native/lib/components";
import { useCurrentVendorProvider } from "../../../../utilities/vendor";
import { AccountFeatureFlag } from "@towersystems/roam-common/lib/generated-types";
import { vendorCanAccessFeature } from "../../../../utilities/feature-flags";
import { useMeProvider } from "@towersystems/roam-common-native/lib/utilities/authentication";

export type SettingsLandingScreenProps = NativeStackScreenProps<
  SettingsRootParamList,
  "SettingsLandingScreen"
>;

export const SettingsLandingScreen = ({
  navigation,
}: SettingsLandingScreenProps) => {
  const { t } = useTranslation("settings");
  const { location } = useActiveLocation();
  const { terminal, reloadTerminal } = useActiveTerminal();
  const { currentVendor } = useCurrentVendorProvider();
  const { me } = useMeProvider();

  const currentUserOwner = Boolean(
    me.roles.find((i) => i.code === "store-owner")
  );

  useFocusEffect(
    React.useCallback(() => {
      reloadTerminal();
    }, [])
  );

  const items: FlatActionListItem[] = [
    {
      key: "General",
      title: t("action.general.label"),
      subTitle: t("action.general.description"),
      onPress: () => navigation.navigate("GeneralSettingsScreen"),
      hidden: !currentUserOwner,
    },
    {
      key: "receipt_templates",
      title: t("action.receipt_templates.label"),
      subTitle: t("action.receipt_templates.description"),
      onPress: () =>
        navigation.navigate("ManageReceiptTemplatesRootScreen", {
          screen: "ManageReceiptTemplatesScreen",
        }),
      hidden: !currentUserOwner,
    },
    {
      key: "dockets",
      title: t("action.docket.label"),
      subTitle: t("action.docket.description"),
      onPress: () =>
        navigation.navigate("DocketSettingsRootScreen", {
          screen: "DocketSettingsScreen",
        }),
      hidden: !currentUserOwner,
    },
    {
      key: "users",
      title: t("action.users.label"),
      subTitle: t("action.users.description"),
      onPress: () =>
        navigation.navigate("SettingsManageUsersRootScreen", {
          screen: "SettingsListStaffUsersScreen",
        }),
      hidden: !currentUserOwner,
    },
    {
      key: "terminals",
      title: t("action.manage_terminals.label"),
      subTitle: t("action.manage_terminals.description"),
      onPress: () =>
        navigation.navigate("ManageTerminalsRootScreen", {
          screen: "ManageTerminalsScreen",
        }),
      hidden: !currentUserOwner,
    },
    {
      key: "manage_locations",
      title: t("action.manage_locations.label"),
      subTitle: t("action.manage_locations.description"),
      onPress: () =>
        navigation.navigate("ManageLocationsRootScreen", {
          screen: "ManageLocationsScreen",
        }),
      hidden: !currentUserOwner,
    },
    {
      key: "setup",
      title: t("action.manage_payment_methods.label"),
      subTitle: t("action.manage_payment_methods.description"),
      onPress: () =>
        navigation.navigate("ManagePaymentMethodsRootScreen", {
          screen: "ListPaymentMethodsScreen",
        }),
      hidden: !currentUserOwner,
    },
  ];

  if (vendorCanAccessFeature(AccountFeatureFlag.POS_LAYBY_UI, currentVendor)) {
    items.push({
      key: "layby",
      title: t("action.layby.label"),
      subTitle: t("action.layby.description"),
      onPress: () => navigation.navigate("LaybySettingsRootScreen"),
      hidden: !currentUserOwner,
    });
  }

  const terminalClosed =
    Boolean(terminal?.terminalCloseTime) || !terminal?.openTerminalClosureId;

  const terminalOpen = !terminalClosed;

  const terminalOpenTime = terminalOpen
    ? `${formatDateToCalendar(terminal.terminalOpenTime?.toDateString() || "")}`
    : undefined;

  return (
    <Page title={t("name")}>
      <Stack vertical>
        <Card title={t("card.my_device.title")}>
          <FlatActionList
            items={[
              {
                isAction: true,
                key: "location",
                title: location?.name || t("action.location.placeholder"),
                subTitle: t("action.location.description"),
                onPress: () =>
                  navigation.navigate("SelectLocationSettingScreen"),
              },
              {
                isAction: true,
                key: `${terminal?.updatedAt?.toDateString()}-terminal}`,
                title: terminal?.name || t("action.terminal.placeholder"),
                subTitle: terminal
                  ? t("action.terminal.description", {
                      datetime: terminalOpenTime,
                    })
                  : undefined,
                onPress: () =>
                  navigation.navigate("TerminalSettingsRootScreen", {
                    screen: "TerminalSettingsLandingScreen",
                  }),
              },
              {
                key: "pos-layout",
                title: t("action.pos_layout.label"),
                subTitle: t("action.pos_layout.description"),
                onPress: () => navigation.navigate("PosLayoutSettingsScreen"),
              },
              {
                key: "shift",
                title: t("action.shift.label"),
                subTitle: t("action.shift.description"),
                onPress: () => navigation.navigate("ShiftSettingsRootScreen"),
              },
            ]}
          />
        </Card>
        {currentUserOwner && (
          <Card title={t("card.general.title")}>
            <FlatActionList items={items} />
          </Card>
        )}

        {!currentUserOwner && (
          <Card title={t("card.security.title")}>
            <FlatActionList
              items={[
                {
                  key: "mfaSettings",
                  title: t("action.mfa_settings.label"),
                  subTitle: t("action.mfa_settings.description"),
                  componentRight: () => {
                    return (
                      <TextButton
                        onPress={() =>
                          navigation.navigate("SetupNative2faRootScreen", {
                            screen: "ListNative2faMethodsScreen",
                          })
                        }
                      >
                        Manage
                      </TextButton>
                    );
                  },
                },
                {
                  key: "changePassword",
                  title: t("action.change_password.label"),
                  subTitle: t("action.change_password.description"),
                  componentRight: () => {
                    return (
                      <TextButton
                        onPress={() =>
                          navigation.navigate("UpdateCredentialsScreen")
                        }
                      >
                        Change Password
                      </TextButton>
                    );
                  },
                },
              ]}
            ></FlatActionList>
          </Card>
        )}

        <Card title={t("card.other.title")}>
          <FlatActionList
            items={[
              {
                key: "manage_data",
                title: t("action.manage_data.label"),
                subTitle: t("action.manage_data.description"),
                onPress: () => navigation.navigate("DataSettingsScreen"),
              },
              {
                key: "manage_api_keys",
                title: t("action.manage_api_keys.label"),
                subTitle: t("action.manage_api_keys.description"),
                onPress: () =>
                  navigation.navigate("ApiKeyVaultRootScreen", {
                    screen: "ApiKeyVaultListScreen",
                  }),
                hidden: !currentUserOwner,
              },
              {
                key: "manage_admin_api_keys",
                title: t("action.manage_admin_api_keys.label"),
                subTitle: t("action.manage_admin_api_keys.description"),
                disabled: false,
                onPress: () =>
                  navigation.navigate("ManageAdminApiSecretsRootScreen", {
                    screen: "ManageAdminApiSecretsScreen",
                  }),
                hidden: !currentUserOwner,
              },
              {
                key: "manage_llm_strategies",
                title: t("action.manage_llm_strategies.label"),
                subTitle: t("action.manage_llm_strategies.description"),
                hidden: !currentUserOwner,
                onPress: () =>
                  navigation.navigate("ManageLlmStrategiesRootScreen", {
                    screen: "ManageLlmStrategiesLandingScreen",
                  }),
              },
            ]}
          />
        </Card>

        {terminal && (
          <PaymentMethodSettingListCard
            terminal={terminal}
            navigation={navigation}
          />
        )}
        <Card title="About">
          <FlatActionList
            items={[
              {
                key: "version",
                title: "Version",
                componentRight: () => {
                  return (
                    <View>
                      <Text>{`V.${packageInfo.version}`}</Text>
                    </View>
                  );
                },
              },
            ]}
          ></FlatActionList>
        </Card>
      </Stack>
    </Page>
  );
};
